/**
 * Single-query editor (page builder Data) — the authoring form for ONE page
 * data source: pick its kind (table / dataset / http / js / static), optionally
 * turn a table into a SERVER-SIDE AGGREGATE (group-by + metrics), and set
 * reactivity (trigger + deps). Extracted from the old inline `QueriesEditor`
 * so it can be reused both in the (transitional) rail list AND the roomy bottom
 * Data dock's master-detail.
 *
 * It owns no state: every edit calls `onChange(partial)` and the parent merges
 * it into the query. `onRemove` deletes the whole query.
 */
import * as React from "react";

import type { PageAggMetric, PageQuery } from "@/lib/pageTypes";

import { CodeField } from "./CodeField";

export const AGG_OPS: PageAggMetric["op"][] = ["count", "sum", "avg", "min", "max"];

/** Default alias an aggregate metric serialises under when none is given —
 *  mirrors the backend's auto-naming so the builder's $queries hints match. */
export function defaultAlias(m: PageAggMetric): string {
  return m.op === "count" ? "count" : `${m.op}_${m.column ?? ""}`;
}

/** Ambient TS for the JS-source Monaco editor — the read-only QuickJS runtime a
 *  `js` query sees (`ctx.db.query`, plus read-only `$page` / `$queries`). */
const JS_QUERY_DTS = `
interface TrayQueryResult { rows: any[]; total: number; data: any; }
interface TrayDb {
  /** Run another page query by id; returns its rows/total/data. */
  query(queryId: string, opts?: { page_size?: number }): TrayQueryResult;
}
interface TrayCtx { db: TrayDb; }
declare const ctx: TrayCtx;
/** Page variables ($page) — read-only here. */
declare const $page: Record<string, any>;
/** Sibling resources ($queries) — read-only here. */
declare const $queries: Record<string, TrayQueryResult>;
`;

export function QueryForm({
  query: q,
  tables,
  datasets = [],
  columnsByTable,
  pageVarNames,
  appQueries = [],
  onChange: patch,
  onRemove,
}: {
  query: PageQuery;
  tables: { id: string; name: string }[];
  datasets?: { id: string; name: string }[];
  columnsByTable: Record<string, string[]>;
  /** Named app-level Queries this page can REFERENCE (ADR-0047 convergence). */
  appQueries?: { id: string; key: string; name: string }[];
  /** Declared $page variables (+ component-written keys) to pick `deps` from.
   *  When omitted, `deps` falls back to a free-text comma list (legacy rail). */
  pageVarNames?: string[];
  onChange: (next: Partial<PageQuery>) => void;
  onRemove: () => void;
}) {
  const kind = q.source_kind ?? "table";
  const isTable = kind === "table";
  const isDataset = kind === "dataset";
  const isHttp = kind === "http";
  const isJs = kind === "js";
  const isLogic = kind === "logic";
  const isQuery = kind === "query";
  const isStatic = kind === "static";
  const cols = isTable ? columnsByTable[q.table_id ?? ""] ?? [] : [];
  const agg = q.aggregate;
  const setAgg = (next: PageQuery["aggregate"] | undefined) => patch({ aggregate: next });
  // Switch source kind, seeding the right id + clearing the others.
  const switchKind = (k: PageQuery["source_kind"]) => {
    if (k === "dataset") patch({ source_kind: "dataset", dataset_id: datasets[0]?.id, table_id: undefined, aggregate: undefined });
    else if (k === "http") patch({ source_kind: "http", method: "GET", table_id: undefined, dataset_id: undefined, aggregate: undefined });
    else if (k === "js") patch({ source_kind: "js", code: "return 1;", table_id: undefined, dataset_id: undefined, aggregate: undefined });
    // `logic` WRITES → never auto-fetched: force `manual` (the backend rejects logic+auto).
    else if (k === "logic") patch({ source_kind: "logic", trigger: "manual", code: "// ctx.input, ctx.user, ctx.fail(msg)\n// ctx.db.create/update/delete/query\nreturn { ok: true };", table_id: undefined, dataset_id: undefined, aggregate: undefined });
    // `query` → a reference to a named app Query (ADR-0047 convergence).
    else if (k === "query") patch({ source_kind: "query", query_id: appQueries[0]?.id, table_id: undefined, dataset_id: undefined, aggregate: undefined });
    else if (k === "static") patch({ source_kind: "static", value: "", table_id: undefined, dataset_id: undefined, aggregate: undefined });
    else patch({ source_kind: "table", table_id: tables[0]?.id, dataset_id: undefined });
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 text-sm">
        <input
          className="w-28 rounded border border-border bg-background px-1.5 py-1"
          value={q.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
        <select
          className="w-24 rounded border border-border bg-background px-1 py-1"
          value={kind}
          onChange={(e) => switchKind(e.target.value as PageQuery["source_kind"])}
        >
          <option value="table">Tabella</option>
          <option value="dataset" disabled={datasets.length === 0}>Proiezione</option>
          <option value="http">HTTP</option>
          <option value="js">Compute</option>
          <option value="logic">Logic</option>
          <option value="query" disabled={appQueries.length === 0}>Query app</option>
          <option value="static">Statico</option>
        </select>
        {isHttp && (
          <input
            className="flex-1 rounded border border-border bg-background px-1.5 py-1 font-mono text-[11px]"
            placeholder="https://api.example.com/items/{{ $page.id }}"
            value={q.url ?? ""}
            onChange={(e) => patch({ url: e.target.value })}
          />
        )}
        {isDataset && (
          <select
            className="flex-1 rounded border border-border bg-background px-1.5 py-1"
            value={q.dataset_id ?? ""}
            onChange={(e) => patch({ dataset_id: e.target.value })}
          >
            {datasets.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}
        {isTable && (
          <select
            className="flex-1 rounded border border-border bg-background px-1.5 py-1"
            value={q.table_id ?? ""}
            onChange={(e) => patch({ table_id: e.target.value })}
          >
            {tables.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
        {isStatic && (
          <span className="flex-1 text-[11px] text-muted-foreground">valore costante →</span>
        )}
        <button className="shrink-0 px-1 text-danger" title="Remove query" onClick={onRemove}>
          ×
        </button>
      </div>

      {/* HTTP source (ADR-0022 Phase B): server-side, SSRF-guarded fetch.
          Method + result_path; headers/query/body each `{{ }}`-templatable
          (the host origin stays literal — only path/query/body are dynamic). */}
      {isHttp && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          <div className="flex items-center gap-1">
            <select
              className="w-24 rounded border border-border bg-background px-1 py-1 text-[11px]"
              value={q.method ?? "GET"}
              onChange={(e) => {
                const method = e.target.value as PageQuery["method"];
                // A writing method can't run on page-load (auto) — the backend
                // requires it manual; flip the trigger so the save validates.
                const writing = method && !["GET", "HEAD"].includes(method);
                patch(writing && (q.trigger ?? "auto") === "auto"
                  ? { method, trigger: "manual" }
                  : { method });
              }}
            >
              {["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <input
              className="flex-1 rounded border border-border bg-background px-1.5 py-1 font-mono text-[11px]"
              placeholder="result_path — es. data.items"
              value={q.result_path ?? ""}
              onChange={(e) => patch({ result_path: e.target.value })}
            />
          </div>
          <KeyValueRows
            label="Header"
            rows={q.headers ?? {}}
            placeholder="Authorization"
            onChange={(headers) => patch({ headers })}
          />
          <KeyValueRows
            label="Query params"
            rows={(q.query as Record<string, string>) ?? {}}
            placeholder="page"
            onChange={(query) => patch({ query })}
          />
          {q.method && !["GET", "HEAD"].includes(q.method) && (
            <div>
              <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Body (JSON)
              </div>
              <JsonDraftField
                value={q.body ?? null}
                empty="null"
                rows={3}
                placeholder='{ "name": "{{ $page.name }}" }'
                onChange={(body) => patch({ body })}
              />
            </div>
          )}
        </div>
      )}

      {/* Compute (js, ADR-0022): a read-only QuickJS body. Logic (ADR-0047): the
          WRITE-capable twin — same editor, ctx also has create/update/delete +
          input/user/fail. Monaco with ctx IntelliSense + expand-to-modal. */}
      {(isJs || isLogic) && (
        <div className="mt-1.5">
          <CodeField
            label={isLogic ? "Codice Logic (scrive + valida)" : "Codice Compute (sola lettura)"}
            value={q.code ?? ""}
            onChange={(code) => patch({ code })}
            ambientDts={JS_QUERY_DTS}
            placeholder={
              isLogic
                ? "// es. var d = ctx.db.query('<id>', { filters: [...] }).rows[0];\n// if (!d) ctx.fail('non trovato');\n// ctx.db.update('<id>', d.id, { stato: 'chiuso' });\n// return { ok: true };"
                : "// es. var r = ctx.db.query('<id>', { page_size: 200 }).rows;\n// return r.length;"
            }
          />
        </div>
      )}

      {/* Query reference (ADR-0047 convergence): bind to a named app Query. */}
      {isQuery && (
        <select
          className="mt-1.5 w-full rounded border border-border bg-background px-1.5 py-1 text-sm"
          value={q.query_id ?? ""}
          onChange={(e) => patch({ query_id: e.target.value })}
        >
          <option value="" disabled>
            — scegli una Query —
          </option>
          {appQueries.map((aq) => (
            <option key={aq.id} value={aq.id}>
              {aq.name} ({aq.key})
            </option>
          ))}
        </select>
      )}

      {/* Static source (ADR-0022): a constant JSON value. */}
      {isStatic && (
        <JsonDraftField
          value={q.value ?? null}
          empty="null"
          rows={2}
          placeholder='valore JSON — es. {"hello": 1} oppure "testo"'
          onChange={(value) => patch({ value })}
        />
      )}

      {/* Aggregate authoring (R2b) — table-only. GROUP BY + count/sum/avg/min/max;
          groups come back under $queries.<name>.rows for a stat/chart. */}
      {isTable && (
        <label className="mt-1.5 flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={Boolean(agg)}
            onChange={(e) =>
              setAgg(e.target.checked ? { metrics: [{ op: "count" }], group_by: [] } : undefined)
            }
          />
          Aggregate (group + metrics)
        </label>
      )}

      {isTable && agg && (
        <div className="mt-1.5 flex flex-col gap-2 border-t border-border/50 pt-1.5">
          {/* Group by — toggle chips over the table's data columns. */}
          <div>
            <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              Group by
            </div>
            {cols.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">No columns.</p>
            ) : (
              <div className="flex flex-wrap gap-1">
                {cols.map((c) => {
                  const on = (agg.group_by ?? []).includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() =>
                        setAgg({
                          ...agg,
                          group_by: on
                            ? (agg.group_by ?? []).filter((g) => g !== c)
                            : [...(agg.group_by ?? []), c],
                        })
                      }
                      className={`rounded border px-1.5 py-0.5 text-[11px] ${
                        on
                          ? "border-accent bg-accent/15 text-accent"
                          : "border-border text-muted-foreground hover:bg-surface"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Metrics — op + column (+ alias). */}
          <div>
            <div className="mb-0.5 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                Metrics
              </span>
              <button
                type="button"
                className="text-[11px] text-accent"
                onClick={() => setAgg({ ...agg, metrics: [...agg.metrics, { op: "count" }] })}
              >
                + Metric
              </button>
            </div>
            {agg.metrics.map((m, mi) => {
              const setMetric = (next: Partial<PageAggMetric>) =>
                setAgg({
                  ...agg,
                  metrics: agg.metrics.map((x, k) => (k === mi ? { ...x, ...next } : x)),
                });
              return (
                <div key={mi} className="mb-1 flex items-center gap-1 text-[12px]">
                  <select
                    className="rounded border border-border bg-background px-1 py-1"
                    value={m.op}
                    onChange={(e) => {
                      const op = e.target.value as PageAggMetric["op"];
                      setMetric(op === "count" ? { op, column: undefined } : { op });
                    }}
                  >
                    {AGG_OPS.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex-1 rounded border border-border bg-background px-1 py-1 disabled:opacity-40"
                    value={m.column ?? ""}
                    disabled={m.op === "count"}
                    onChange={(e) => setMetric({ column: e.target.value || undefined })}
                  >
                    <option value="">{m.op === "count" ? "— (all rows)" : "(column)"}</option>
                    {cols.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input
                    className="w-20 rounded border border-border bg-background px-1 py-1"
                    placeholder={defaultAlias(m)}
                    value={m.alias ?? ""}
                    onChange={(e) => setMetric({ alias: e.target.value || undefined })}
                  />
                  <button
                    type="button"
                    className="px-1 text-danger disabled:opacity-30"
                    title="Remove metric"
                    disabled={agg.metrics.length <= 1}
                    onClick={() => setAgg({ ...agg, metrics: agg.metrics.filter((_, k) => k !== mi) })}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reactivity (ADR-0022 Phase C): trigger + deps. */}
      <div className="mt-1.5 flex items-center gap-2 border-t border-border/50 pt-1.5 text-[11px]">
        <span className="text-muted-foreground">Trigger</span>
        <select
          aria-label="Trigger"
          className="rounded border border-border bg-background px-1 py-1"
          value={q.trigger ?? "auto"}
          onChange={(e) => patch({ trigger: e.target.value as PageQuery["trigger"] })}
        >
          <option value="auto">auto (on load + deps)</option>
          <option value="manual">manual (on event)</option>
        </select>
        {pageVarNames === undefined ? (
          // Legacy rail (no var list threaded) → free-text comma list.
          <input
            aria-label="Depends on"
            className="flex-1 rounded border border-border bg-background px-2 py-1"
            placeholder="deps: page vars (comma-sep, e.g. selectedRow, search)"
            value={(q.deps ?? []).join(", ")}
            onChange={(e) =>
              patch({
                deps: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        ) : (
          <DepsPicker deps={q.deps ?? []} pageVarNames={pageVarNames} onChange={(deps) => patch({ deps })} />
        )}
      </div>
    </div>
  );
}

/**
 * A JSON value editor that you can actually TYPE in. The naive
 * `value={JSON.stringify(x)}` + `onChange=JSON.parse` pattern fights the user:
 * mid-edit the text is transiently invalid JSON, the parse throws, the value is
 * unchanged, and the controlled input snaps back — so deleting a quote freezes
 * the field. Here a local `draft` string holds the raw text; it commits to the
 * parent only when the JSON parses, and only re-syncs from the prop on an
 * EXTERNAL change (switching query / undo), never on our own commits.
 */
function JsonDraftField({
  value,
  empty,
  rows = 2,
  placeholder,
  onChange,
}: {
  value: unknown;
  /** Serialization for an "empty" textarea (e.g. `"{}"` / `"null"`). */
  empty: string;
  rows?: number;
  placeholder?: string;
  onChange: (next: unknown) => void;
}) {
  const serialize = (v: unknown) => JSON.stringify(v ?? null);
  const [draft, setDraft] = React.useState(() => serialize(value));
  const [invalid, setInvalid] = React.useState(false);
  const lastCommitted = React.useRef(draft);

  React.useEffect(() => {
    const s = serialize(value);
    if (s !== lastCommitted.current) {
      setDraft(s);
      setInvalid(false);
      lastCommitted.current = s;
    }
  }, [value]);

  const onText = (t: string) => {
    setDraft(t);
    try {
      const parsed = JSON.parse(t.trim() === "" ? empty : t);
      setInvalid(false);
      lastCommitted.current = serialize(parsed);
      onChange(parsed);
    } catch {
      setInvalid(true); // keep typing — don't fight the input while it's invalid
    }
  };

  return (
    <div className="mt-1.5">
      <textarea
        className={`w-full rounded border bg-background px-1.5 py-1 font-mono text-[11px] ${
          invalid ? "border-danger" : "border-border"
        }`}
        rows={rows}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => onText(e.target.value)}
      />
      {invalid && (
        <p className="mt-0.5 text-[10px] text-danger">
          JSON non valido — le stringhe vanno tra apici doppi (es. &quot;testo&quot;).
        </p>
      )}
    </div>
  );
}

/**
 * Key/value rows for http headers + query params — each VALUE is a `{{ }}`
 * template the client evaluates against the page scope (so a header can carry a
 * token from `$page` and a query param can read a selection). Empty-key rows are
 * dropped; a trailing blank row lets you add another.
 */
function KeyValueRows({
  label,
  rows,
  placeholder,
  onChange,
}: {
  label: string;
  rows: Record<string, string>;
  placeholder: string;
  onChange: (next: Record<string, string>) => void;
}) {
  const entries = Object.entries(rows);
  const commit = (pairs: [string, string][]) => {
    const out: Record<string, string> = {};
    for (const [k, v] of pairs) if (k.trim()) out[k] = v;
    onChange(out);
  };
  const view = [...entries, ["", ""] as [string, string]];
  return (
    <div>
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </div>
      <div className="flex flex-col gap-1">
        {view.map(([k, v], i) => (
          <div key={i} className="flex items-center gap-1">
            <input
              className="w-28 rounded border border-border bg-background px-1.5 py-1 font-mono text-[11px]"
              placeholder={placeholder}
              value={k}
              onChange={(e) => {
                const next = [...entries];
                if (i < next.length) next[i] = [e.target.value, v];
                else next.push([e.target.value, ""]);
                commit(next);
              }}
            />
            <input
              className="flex-1 rounded border border-border bg-background px-1.5 py-1 font-mono text-[11px]"
              placeholder="valore o {{ $page.x }}"
              value={v}
              onChange={(e) => {
                const next = [...entries];
                if (i < next.length) next[i] = [k, e.target.value];
                else next.push([k, e.target.value]);
                commit(next);
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `deps` picker — page variables a query reacts to, as toggle chips over the
 * known $page surface (declared vars + component-written keys) instead of a
 * free-text comma list. A dep not in that surface (legacy / typo) shows as a
 * removable amber chip so it's visible + fixable.
 */
function DepsPicker({
  deps,
  pageVarNames,
  onChange,
}: {
  deps: string[];
  pageVarNames: string[];
  onChange: (deps: string[]) => void;
}) {
  const toggle = (name: string) =>
    onChange(deps.includes(name) ? deps.filter((d) => d !== name) : [...deps, name]);
  const unknown = deps.filter((d) => !pageVarNames.includes(d));

  return (
    <div className="flex flex-1 flex-wrap items-center gap-1">
      {pageVarNames.length === 0 && unknown.length === 0 ? (
        <span className="text-[11px] text-muted-foreground">
          Nessuna variabile di pagina — creane una nella tab Stato.
        </span>
      ) : (
        pageVarNames.map((name) => {
          const on = deps.includes(name);
          return (
            <button
              key={name}
              type="button"
              onClick={() => toggle(name)}
              className={`rounded border px-1.5 py-0.5 text-[11px] ${
                on
                  ? "border-accent bg-accent/15 text-accent"
                  : "border-border text-muted-foreground hover:bg-surface"
              }`}
            >
              {name}
            </button>
          );
        })
      )}
      {unknown.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => toggle(d)}
          title="Variabile non dichiarata — clic per rimuovere dalle deps"
          className="rounded border border-warning/50 bg-warning/10 px-1.5 py-0.5 text-[11px] text-warning"
        >
          {d} ×
        </button>
      ))}
    </div>
  );
}
